/**
 * What THIS BUNDLE is built from, and which asset is executing (#490).
 *
 * The failure this closes was reproduced on 2026-08-24: Development served a
 * three-day-old bundle while `main` had advanced twice, and nothing the browser
 * could read said so. `/config.json` reported `release.commit = "dev"` — a
 * moving label — so a screenshot could not be pinned to source and "the feature
 * was never implemented" was indistinguishable from "you are looking at an old
 * deployment".
 *
 * TWO VALUES, TWO DIFFERENT KINDS OF CLAIM, and keeping them apart is the whole
 * design:
 *
 *   `EDITOR_WEB_BUILD.editorWebSourceCommit` is substituted into this file at
 *   BUILD time by Vite's `define` (from `--build-arg GIT_SHA`, see
 *   apps/editor-web/Dockerfile). It travels inside the hashed asset, so it is
 *   covered by the image digest and cannot be changed by a ConfigMap, an
 *   overlay edit or anything else that happens after the build. It describes
 *   the code that is RUNNING.
 *
 *   `/config.json`'s `release.commit` is written into the overlay by
 *   `scripts/pin-image-digests.py` in the same edit that pins the image
 *   digests. It describes what the deployment INTENDED to run, and it is
 *   deployment-writable — which is exactly why it is not sufficient on its own.
 *
 * A stale deployment is the case where they disagree, and `deploymentIdentity`
 * in `src/api/deploymentIdentity.ts` is where the comparison is made. Neither
 * value alone can detect it.
 *
 * WHY THE COMMIT IS IN A PUBLIC ASSET AT ALL, given #490's "no public version
 * disclosure" non-goal. An SPA's build identity is necessarily inside the asset
 * it serves — there is no other place a browser could read it from without a
 * network round trip that would itself be a version endpoint. What the non-goal
 * forbids is publishing a RELEASE surface (an unauthenticated `/version`
 * announcing product versions); the same ticket requires `/config.json`, which
 * is fetched unauthenticated, to carry the exact commit rather than `dev`. The
 * human-facing surface built on this data is authenticated
 * (`GET /v1/support/deployment-identity` and the support panel behind it).
 */

/** Substituted by `define` in vite.config.ts. See the note above on why these
 *  names do not survive into the built bundle. */
declare const __EDITOR_WEB_SOURCE_COMMIT__: string;
declare const __EDITOR_WEB_BUILT_AT__: string;

const GIT_SHA = /^[0-9a-f]{40}$/;

/**
 * The bundle's baked identity.
 *
 * AN OBJECT LITERAL WITH THESE EXACT PROPERTY NAMES, not two bare constants,
 * and that is load-bearing rather than stylistic. `define` substitutes the
 * value textually, so what reaches `dist/` is a 40-character hex string
 * literal — which is precisely the shape `credentialShapedValue` reports as "a
 * hex-encoded digest or key", and #45's built-bundle credential scan (the
 * invariant suite under `src/invariants/`, which this file may not name: the
 * scan treats a credential's NAME in a bundle input as a finding, and its own
 * filename is one) is mandatory on every frontend PR. The allowance that
 * exempts this one value is keyed on the property name
 * immediately preceding it, and minifiers do not rename object property keys.
 * Rename these keys and the bundle scan fails; move the substitution somewhere
 * the key does not precede it and the bundle scan fails. Both are the correct
 * outcome — an unlabelled 40-hex literal in a published asset cannot be told
 * apart from a leaked key.
 */
export const EDITOR_WEB_BUILD = {
  editorWebSourceCommit: __EDITOR_WEB_SOURCE_COMMIT__,
  editorWebBuiltAt: __EDITOR_WEB_BUILT_AT__,
} as const;

/**
 * `true` when the bundle carries a real commit id rather than the `unknown`
 * placeholder a local `npm run build` produces.
 *
 * Shape-checked, not merely non-empty: `unknown`, `dev` and `latest` are all
 * non-empty strings, and every one of them is the moving-label answer #490
 * exists to refuse.
 */
export function hasVerifiedWebCommit(): boolean {
  return GIT_SHA.test(EDITOR_WEB_BUILD.editorWebSourceCommit);
}

/**
 * The filename of the JavaScript asset this code is executing from.
 *
 * `import.meta.url` is the URL the browser actually loaded, so this is an
 * OBSERVATION rather than a claim: it cannot be made to describe an asset that
 * is not running. Vite content-hashes the filename, so a changed bundle is a
 * changed asset id, and comparing it against the entry `<script src>` in the
 * served `index.html` is what distinguishes "the whole deployment is stale"
 * from "index.html and the assets it points at disagree".
 *
 * Returns `null` where there is nothing meaningful to report — under Vitest the
 * module URL is a `file://` path to the source file, and reporting THAT as the
 * served asset would be a confident wrong answer.
 */
export function servedAssetId(): string | null {
  const url = import.meta.url;
  if (!url.startsWith("http://") && !url.startsWith("https://")) return null;
  try {
    const name = new URL(url).pathname.split("/").pop() ?? "";
    return name.endsWith(".js") ? name : null;
  } catch {
    return null;
  }
}
